import { useContext, useState } from "react";

import "./style.scss";
import Alert from "shared/ui/Alert";
import Button from "shared/ui/Button";
import DocumentPreview from "../../DocumentPreview";
import { DocumentPreviewNumberType, TrustfullResponse } from "shared/types/types";
import { ReactComponent as IconChecked } from "../../../assets/img/icons/icon_status-Checked.svg";
import { ReactComponent as IconInfo } from "../../../assets/img/icons/icon_Info.svg";
import { ReactComponent as IconWarning } from "../../../assets/img/icons/icon_status-Warning.svg";
import { ReactComponent as IconLoader } from "../../../assets/img/icons/icon_Loader.svg";
import { UserContext } from "features/account";
import { documentRequest } from "features/invitation";
import { Button_Style, Button_Type } from "shared/ui/Button/Button";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { ReactComponent as IconDownload } from "../../../assets/img/icons/icon_Download.svg";

type Props = {
  result: DocumentPreviewNumberType;
  trustfullData?: TrustfullResponse;
  refresh?: () => void;
  onRefreshTrustfull?: () => void;
  isTrustfullLoading?: boolean;
  showTrusfullVerification?: boolean;
};

export default function VerifyDatabaseDocumentResult({
                                                       result,
                                                       trustfullData,
                                                       refresh,
                                                       onRefreshTrustfull,
                                                       isTrustfullLoading = false,
                                                       showTrusfullVerification = true,
                                                     }: Props) {
  const { userType, user } = useContext(UserContext);
  const [requestId, setRequestId] = useState<string>("");
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showNumber, setShowNumber] = useState(true);

  const idTracker = async () => {
    try {
      const response = await documentRequest(result?.numeroDocument ?? trustfullData?.dataBaseResult?.cardNumber, userType);
      setRequestId(response.data.id);
      setRequestSent(true);
    } catch (error: any) {
      console.log(error.response.data);
    }
  };

  const downloadReport = () => {
    setIsDownloading(true);
    setShowNumber(true);

    const element = document.body;

    html2canvas(element, { scale: 1.5 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/jpeg", 0.6);
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      pdf.setFillColor(240, 245, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, "F");

      const companyName = `${user?.companyName || "Mon Entreprise"}`;
      const title = "Rapport d’Analyse de Document";
      const dateStr = new Date().toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });

      pdf.setTextColor(40, 40, 70);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.text(companyName, pageWidth / 2, 20, { align: "center" });

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(14);
      pdf.text(title, pageWidth / 2, 30, { align: "center" });

      pdf.setFontSize(11);
      pdf.text(`Date du rapport : ${dateStr}`, pageWidth / 2, 38, { align: "center" });

      pdf.setDrawColor(100, 140, 220);
      pdf.setLineWidth(1);
      pdf.line(20, 45, pageWidth - 20, 45);

      const margin = 15;
      const contentX = margin;
      const contentY = 55;
      const contentW = pageWidth - margin * 2;
      const contentH = pageHeight - 80;

      pdf.setFillColor(255, 255, 255);
      pdf.roundedRect(contentX, contentY, contentW, contentH, 6, 6, "F");

      const imgWidth = contentW - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let positionY = contentY + 5;

      if (imgHeight <= contentH) {
        const centeredX = contentX + (contentW - imgWidth) / 2;
        pdf.addImage(imgData, "JPEG", centeredX, positionY, imgWidth, imgHeight);
      } else {
        let heightLeft = imgHeight;
        let position = positionY;

        while (heightLeft > 0) {
          const centeredX = contentX + (contentW - imgWidth) / 2;
          pdf.addImage(imgData, "JPEG", centeredX, position, imgWidth, imgHeight);

          heightLeft -= pageHeight - contentY;
          position -= pageHeight;

          if (heightLeft > 0) {
            pdf.addPage();

            pdf.setFillColor(240, 245, 255);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");

            pdf.setFillColor(255, 255, 255);
            pdf.roundedRect(contentX, contentY, contentW, contentH, 6, 6, "F");

            position = contentY + 5;
          }
        }
      }

      pdf.setTextColor(80, 80, 80);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "italic");
      pdf.text(
        "Rapport généré automatiquement - confidentiel @idprotect.fr",
        pageWidth / 2,
        pageHeight - 10,
        { align: "center" }
      );

      pdf.save("rapport.pdf");
      setIsDownloading(false);
    });
  };

  // Get Trustfull status based on score
  const getTrustfullStatus = (scoreCluster: string) => {
    if (scoreCluster?.toLowerCase() === "review" || scoreCluster?.toLowerCase() === "moderate") {
      if (!trustfullData?.dataBaseResult?.founded) {
        return {
          label: "Dark Web",
          description: "Ces informations ont été compromises et circulent sur le dark web",
          type: "darkweb",
          icon: <IconWarning fill="#FF5326" width="1.5rem" className="flex-none" />
        };
      } else {
        return {
          label: "Conforme",
          description: "Les informations semblent fiables et sans risque apparent",
          type: "conforme",
          icon: <IconChecked fill="#17EB79" width="1.5rem" className="flex-none" />
        };
      }
    } else if (scoreCluster?.toLowerCase() === "bad" || scoreCluster?.toLowerCase() === "low") {
      return {
        label: "Usurpation suspectée",
        description: "Risque élevé d'usurpation d'identité, soyez vigilant",
        type: "usurpation",
        icon: <IconWarning fill="#FF5326" width="1.5rem" className="flex-none" />
      };

    } else if (scoreCluster?.toLowerCase() === "poor" || scoreCluster?.toLowerCase() === "very_low") {
      return {
        label: "Fraude avérée",
        description: "Risque élevé de fraude, soyez vigilant",
        type: "fraude",
        icon: <IconWarning fill="#FF5326" width="1.5rem" className="flex-none" />
      };
    } else if (scoreCluster?.toLowerCase() === "high" || scoreCluster?.toLowerCase() === "very_high" || scoreCluster?.toLowerCase() === "good") {
      return {
        label: "Conforme",
        description: "Les informations semblent fiables et sans risque apparent",
        type: "conforme",
        icon: <IconChecked fill="#17EB79" width="1.5rem" className="flex-none" />
      };
    }
    else {
      return {
        label: "Inconnu",
        description: "inconnu d'ID Protect",
        type: "inconnu",
        icon: <IconWarning fill="#171C60" width="1.5rem" className="flex-none" />
      };
    }
  };

  // Get dataBaseResult based on ID Protect DB
  const getDataBaseResult = (where: string) => {
    if (where === 'followed') {
      return {
        label: "Document en Suivi",
        description: "Cette personne a un document enregistré en suivi",
      };
    } else if (where === 'perte') {
      return {
        label: "Document en Perte",
        description: "Cette personne a un document enregistré comme perdu",
      };
    } else {
      return {
        label: "client à ID Protect",
        description: "Cette personne est cliente chez ID Protect",
      };
    }
  };

  return (
    <div className="d-flex flex-column align-items-end justify-content-between">
      <div className="w-100">

        {result ? (
          <>
            {/* Document Preview Section */}
            <DocumentPreview
              document={result}
              type={result?.status}
              showNumber={showNumber}
            />
            {/* Dark Web Section */}
            {result?.darkWeb ? (
              <div>
                <Alert type="error">Soyez vigilant</Alert>
                <div className="d-flex gap-05 align-items-center">
                  <IconWarning
                    fill="#FF5326"
                    width="1.5rem"
                    className="flex-none"
                  />
                  Le document est présent sur le dark web
                </div>
              </div>
            ) : (
              <div className="d-flex gap-05 align-items-center">
                <IconChecked
                  fill="#17EB79"
                  width="1.5rem"
                  className="flex-none"
                />
                Le document n'est pas présent sur le dark web
              </div>
            )}

            {/* Document Status Section */}
            {result?.status === "not_lost_or_sloten" && (
              <>
                <div className="d-flex gap-05 align-items-center mb-2">
                  <IconChecked
                    fill="#17EB79"
                    width="1.5rem"
                    className="flex-none"
                  />
                  Document connu des services ID Protect, ne fait l'objet d'aucun signalement spécifique.
                </div>
              </>
            )}
            {result?.status === "lost_or_sloten" && (
              <>
                <Alert type="error">Nous vous conseillons de ne pas entrer en relation sur présentation de ce document.</Alert>
                <div className="d-flex gap-05 align-items-center">
                  <IconWarning
                    fill="#FF5326"
                    width="1.5rem"
                    className="flex-none"
                  />
                  Le document est déclaré perdu/volé
                </div>
              </>
            )}
            {result?.status === "not_found" && (
              <div className="d-flex gap-05 align-items-center">
                <IconInfo
                  fill="#1956A8"
                  width="1.5rem"
                  className="flex-none"
                />
                Le document est absent de la base de données ID Protect
              </div>
            )}
            {result?.status === "locked_document" && (
              <div className="d-flex gap-05 align-items-center">
                <IconWarning
                  fill="#FF5326"
                  width="1.5rem"
                  className="flex-none"
                />
                Le document est bloqué par l'utilisateur
              </div>
            )}
            {result?.status === "fraude" && (
              <>
                <Alert type="error">Suspiscion de fraude</Alert>
                <div className="d-flex gap-05 align-items-center">
                  <IconWarning
                    fill="#FF5326"
                    width="1.5rem"
                    className="flex-none"
                  />
                  Le document a fait l'objet d'un signalement pour usurpation d'identité
                </div>
              </>
            )}

          </>
        ) : (
          <>
            <div className={"text-muted-foreground px-4 text-italic"}>
              Aucune donnée concernant le document n'a été fournie
            </div>
          </>
        )}


        {/* Trustfull Results Section - Simplified & Charismatic */}
        {showTrusfullVerification && (
          <div className="mt-4 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="mb-0">Vérification personne</h3>
            </div>

            {/* Loading State */}
            {isTrustfullLoading && !trustfullData && (
              <div className="text-center py-4">
                <IconLoader
                  width="2rem"
                  height="2rem"
                  className="spin mb-2"
                  style={{ animation: 'spin 1s linear infinite' }}
                />
                <p className="text-muted mb-0">Vérification en cours...</p>
              </div>
            )}

            {/* Trustfull Result */}
            {trustfullData ? (
              <div className={`trustfull-result p-4 rounded ${
                getTrustfullStatus(trustfullData.score?.score_cluster).type === 'conforme' ? 'bg-success-light' : 'bg-danger-light'
              }`}>
                <div className="d-flex align-items-center gap-1 mb-3">
                  {getTrustfullStatus(trustfullData.score.score_cluster).icon}
                  <div className={"d-flex flex-column"}>
                    <div className={"text-sm text-uppercase bold"}>
                      {getTrustfullStatus(trustfullData.score.score_cluster).label}
                    </div>
                    {getTrustfullStatus(trustfullData.score?.score_cluster).type === 'conforme' && trustfullData?.dataBaseResult?.founded && (
                      <div className={"text-muted-foreground text-xs"}>
                        {getDataBaseResult(trustfullData?.dataBaseResult?.where ?? '').label}
                      </div>
                    )}
                  </div>
                </div>

                <p className="mb-0 text-italic">
                  {getTrustfullStatus(trustfullData.score.score_cluster).description}
                </p>
                {/*{trustfullData?.dataBaseResult?.founded && (
                <p className="mb-0 text-italic">
                  {getDataBaseResult(trustfullData?.dataBaseResult?.where ?? "").description}
                </p>
              )}*/}


                {/* Additional */}
                {/* {(trustfullData?.email?.data_breaches_count > 0 || trustfullData?.phone?.data_breaches_count > 0) && (
                <div className="pt-2 border-top">
                  <hr />
                  <small className="text-sm">
                    {trustfullData?.email?.data_breaches_count > 0 && (
                      <span>{trustfullData.email.value} - {trustfullData.email.data_breaches_count} fuite(s) </span>
                    )}
                    {trustfullData?.phone?.data_breaches_count > 0 && (
                      <span>{trustfullData.phone.value} - {trustfullData.phone.data_breaches_count} fuite(s)</span>
                    )}
                  </small>
                </div>
              )}*/}
              </div>
            ) : (
              <div className={"text-muted-foreground px-4 text-italic"}>
                Aucune donnée concernant la personne n'a été fournie
              </div>
            )}

            {((getTrustfullStatus(trustfullData?.score?.score_cluster ?? "")?.type === 'conforme' && trustfullData?.dataBaseResult?.founded && trustfullData?.dataBaseResult?.where !== 'user-id-protect')
                || (result?.status === "not_lost_or_sloten"))
              && (
                <>
                  <div className="card card--info mb-2 mt-2">
                    <div className="card-body">
                      <h5 className="card-title">ID Tracker</h5>
                      <p className="card-text mb-2">
                        Vous pouvez avertir le titulaire que son identité a été utilisée pour accéder à vos services.
                      </p>
                      <Button
                        label="Avertir"
                        onClick={idTracker}
                        disabled={requestSent}
                      />
                    </div>
                  </div>
                  <div>
                    {requestSent && <Alert type="success">Le mail a été envoyée avec succès</Alert>}
                  </div>
                </>
              )}
          </div>
        )}

      </div>

      <div className="w-100 mt-3">
        <Button
          label="Télécharger le rapport"
          onClick={downloadReport}
          type={Button_Type.PRIMARY}
          style={Button_Style.OUTLINED}
          isLoader={isDownloading}
          icon={<IconDownload
            fill="#FF5326"
            width="1.5rem"
            className="flex-none" />
          }
          className="mb-2 w-100"
        />
        <Button
          label="Une autre vérification"
          onClick={refresh}
          type={Button_Type.SECONDARY}
          className="w-100"
        />
      </div>

      {/* Add spin animation style */}
      <style>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        .bg-success-light {
          background-color: rgba(18, 188, 97, 0.1);
          border: 1px solid rgba(18, 188, 97, 0.2);
        }
        .bg-danger-light {
          background-color: rgba(255, 83, 38, 0.1);
          border: 1px solid rgba(255, 83, 38, 0.2);
        }
      `}</style>
    </div>
  );
}